import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { useFormattedRates } from '@/hooks/useFormattedRates';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { Amount } from '@/types/amount';
import { ConversionDetails, FeeType, SpreadFee } from '@/types/transaction';

interface Props {
  readonly from: Amount;
  readonly to: Amount;
  readonly conversionDetails?: ConversionDetails | null;
  readonly spreadFees?: ReadonlyArray<SpreadFee>;
  readonly fxLockSpreadPercentage?: number | null;
}

const PERCENT_FORMATTER_CONFIG = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  style: 'percent' as const,
};

const SPREAD_FORMATTER_CONFIG = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

export const RatesTransactionDetailsItem: React.FC<Props> = (props: Props): React.ReactElement => {
  const { from, to, conversionDetails, spreadFees, fxLockSpreadPercentage } = props;
  const tr = useTranslator();

  const formattedRates = useFormattedRates(from, to, conversionDetails);
  const percentFormatter = useLocalizedFormatter(PERCENT_FORMATTER_CONFIG);
  const spreadFormatter = useLocalizedFormatter(SPREAD_FORMATTER_CONFIG);

  const calizaRateTooltip: string | null =
    conversionDetails && conversionDetails.calizaRate > 0
      ? tr('CALIZA_RATE_TOOLTIP', percentFormatter.format(conversionDetails.calizaRate / 100))
      : null;

  const fxLockTooltip: string | null =
    fxLockSpreadPercentage != null && fxLockSpreadPercentage > 0
      ? tr('FX_LOCK_RATE_TOOLTIP', spreadFormatter.format(fxLockSpreadPercentage))
      : null;

  const tooltipItems = [calizaRateTooltip, fxLockTooltip].filter(Boolean) as string[];
  const tooltipText: React.ReactNode | null =
    tooltipItems.length === 0 ? null : tooltipItems.length === 1 ? (
      tooltipItems[0]
    ) : (
      <>
        {tooltipItems.map(
          (item: string, index: number): React.ReactElement => (
            <React.Fragment key={index}>
              {index > 0 && <br />}
              {item}
            </React.Fragment>
          ),
        )}
      </>
    );

  const integratorSpreadFee = spreadFees?.find(
    (fee: SpreadFee): boolean => fee.type === FeeType.INTEGRATOR_SPREAD,
  );
  const integratorSpreadPercent = integratorSpreadFee
    ? Number(integratorSpreadFee.spreadBasisPoint)
    : null;
  const customerRateTooltip =
    integratorSpreadPercent !== null
      ? tr('CUSTOMER_RATE_TOOLTIP', percentFormatter.format(integratorSpreadPercent))
      : null;

  return (
    <Box>
      <Stack direction="row" color="text.primary" gap={0.5} alignItems="center">
        <Typography variant="body2">
          {tr('MID_MARKET_RATE')}: {formattedRates.market}
        </Typography>
        <Tooltip title={tr('MID_MARKET_RATE_SOURCE')} placement="top-start">
          <Box component="span" sx={styles.infoIcon}>
            <InfoIcon fill="currentColor" width={16} height={16} />
          </Box>
        </Tooltip>
      </Stack>
      <Stack direction="row" color="text.primary" gap={0.5} alignItems="center">
        <Typography variant="body2">
          {tr('CALIZA_RATE')}: {formattedRates.client}
        </Typography>
        {tooltipText && (
          <Tooltip title={tooltipText} placement="top-start">
            <Box component="span" sx={styles.infoIcon}>
              <InfoIcon fill="currentColor" width={16} height={16} />
            </Box>
          </Tooltip>
        )}
      </Stack>
      {formattedRates.integrator && (
        <Stack direction="row" color="text.primary" gap={0.5} alignItems="center">
          <Typography variant="body2">
            {tr('FX_MARKUP_CUSTOMER_RATE')}: {formattedRates.integrator}
          </Typography>
          {customerRateTooltip && (
            <Tooltip title={customerRateTooltip} placement="top-start">
              <Box component="span" sx={styles.infoIcon}>
                <InfoIcon fill="currentColor" width={16} height={16} />
              </Box>
            </Tooltip>
          )}
        </Stack>
      )}
      {formattedRates.effective && (
        <Stack direction="row" color="text.primary" gap={0.5} alignItems="center">
          <Typography variant="body2">VET: {formattedRates.effective}</Typography>
          <Tooltip title={tr('VET_TOOLTIP')} placement="bottom-start">
            <Box component="span" sx={styles.infoIcon}>
              <InfoIcon fill="currentColor" width={16} height={16} />
            </Box>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  infoIcon: {
    display: 'inline-flex',
    verticalAlign: 'middle',
  },
};
